import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Spinner } from '@nextui-org/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { useAdminClient, type CatalogDataset, type CatalogDomain, type CatalogMetric } from './api';
import { ErrorLine, TabIntro, columnAction, useAdminLoad } from './shared';

/** A metric belongs to a dataset when it names it in full or by its name within the domain. */
function readsDataset(metric: CatalogMetric, dataset: CatalogDataset): boolean {
  const short = dataset.name.includes('.') ? dataset.name.slice(dataset.name.indexOf('.') + 1) : dataset.name;
  return metric.dataset === dataset.name || metric.dataset === short;
}

/**
 * Catalog (read-only): the governed catalog's domains and datasets, and for the selected dataset
 * its grain, bindings, columns with how each reaches an agent, and its metrics.
 */
export function CatalogTab({ baseUrl }: { readonly baseUrl: string }) {
  const { t } = useTranslation();
  const client = useAdminClient(baseUrl);
  const { data, loading, error, reload } = useAdminLoad(client, (c, signal) => c.catalog(signal));
  const domains = useMemo(() => data?.domains ?? [], [data]);
  const [selected, setSelected] = useState<string | null>(null);

  const all = domains.flatMap((domain) => domain.datasets.map((dataset) => ({ domain, dataset })));
  const current = all.find((entry) => entry.dataset.name === selected) ?? all[0] ?? null;

  return (
    <div className="flex flex-col gap-4 px-6 pb-6">
      <TabIntro
        title={t('Catalog')}
        actions={(
          <CtyButton variant="flat" size="sm" onPress={() => void reload()} isDisabled={loading || !client}>
            {t('Refresh')}
          </CtyButton>
        )}
      >
        <span className="text-sm text-default-500">
          {t('The datasets AI agents can query through the data layer, and how each column reaches them.')}
        </span>
        <span className="text-xs text-default-400">{t('Read-only. Tenant edits to the catalog come later.')}</span>
      </TabIntro>

      {loading && <Spinner size="sm" className="self-start" />}
      <ErrorLine message={error} />
      {data && domains.length === 0 && <p className="text-sm text-default-500">{t('The catalog has no datasets yet.')}</p>}

      {current && (
        <div className="flex flex-col gap-4 lg:flex-row">
          <nav aria-label={t('Domains')} className="flex shrink-0 flex-col gap-3 lg:w-72">
            {domains.map((domain) => (
              <div key={domain.domain} className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-foreground" title={domain.description}>{domain.domain}</span>
                {domain.datasets.map((dataset) => {
                  const active = dataset.name === current.dataset.name;
                  return (
                    <button
                      key={dataset.name}
                      type="button"
                      aria-current={active ? 'true' : undefined}
                      onClick={() => setSelected(dataset.name)}
                      className={`rounded-medium px-2 py-1 text-left font-mono text-xs ${active ? 'bg-primary-50 text-primary' : 'text-default-600 hover:bg-default-100'}`}
                    >
                      {dataset.name}
                    </button>
                  );
                })}
                {domain.datasets.length === 0 && <span className="px-2 text-xs text-default-400">{t('No datasets')}</span>}
              </div>
            ))}
          </nav>
          <DatasetDetail domain={current.domain} dataset={current.dataset} />
        </div>
      )}
    </div>
  );
}

function DatasetDetail({ domain, dataset }: { readonly domain: CatalogDomain; readonly dataset: CatalogDataset }) {
  const { t } = useTranslation();
  const metrics = domain.metrics.filter((metric) => readsDataset(metric, dataset));
  const facts: Array<[string, string]> = [
    [t('Domain'), domain.domain],
    [t('Grain'), dataset.grain || '—'],
    [t('Date column'), dataset.time_column || '—'],
    [t('Database'), dataset.database || '—'],
    [t('Tenant isolation'), dataset.tenant_isolation || '—'],
    [t('Owner'), dataset.owner
      ? `${dataset.owner.column} (${t('identifies by')}: ${dataset.owner.identifies_by.join(', ') || '—'})`
      : t('None')],
    [t('Entity'), dataset.entity ? `${dataset.entity.column} → ${dataset.entity.type}` : t('None')],
  ];

  return (
    <section aria-label={dataset.name} className="flex min-w-0 flex-1 flex-col gap-3 rounded-medium border border-default-200 p-3">
      <span className="font-mono text-sm font-semibold">{dataset.name}</span>
      {dataset.description ? <p className="text-sm text-default-500">{dataset.description}</p> : null}
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        {facts.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-default-500">{label}</dt>
            <dd className="break-words font-mono text-xs leading-5">{value}</dd>
          </div>
        ))}
      </dl>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs text-default-500">
            <tr>
              <th className="py-1 pr-4 font-medium">{t('Column')}</th>
              <th className="py-1 pr-4 font-medium">{t('Type')}</th>
              <th className="py-1 pr-4 font-medium">{t('Description')}</th>
              <th className="py-1 pr-4 font-medium">{t('Classification')}</th>
              <th className="py-1 font-medium">{t('AI visibility')}</th>
            </tr>
          </thead>
          <tbody>
            {dataset.columns.map((column) => {
              const action = columnAction(column.action);
              return (
                <tr key={column.name} className="border-t border-default-100">
                  <td className="py-1 pr-4 font-mono text-xs">{column.name}</td>
                  <td className="py-1 pr-4 text-xs text-default-500">{column.type}</td>
                  <td className="py-1 pr-4 text-xs text-default-500">{column.description}</td>
                  <td className="py-1 pr-4 text-xs text-default-500">{column.classification}</td>
                  <td className="py-1">
                    <Chip size="sm" variant="flat" color={action.color}>{t(action.label)}</Chip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {dataset.excluded_columns > 0 && (
        <p className="text-xs text-default-500">
          {t('{{count}} more columns are never exposed', { count: dataset.excluded_columns })}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <span className="text-sm font-semibold text-foreground">{t('Metrics')}</span>
        {metrics.length === 0 && <p className="text-xs text-default-500">{t('No metrics read this dataset.')}</p>}
        {metrics.map((metric) => (
          <div key={metric.key} className="flex flex-col gap-1 border-t border-default-100 pt-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs font-semibold">
                {metric.key}({metric.params.join(', ')})
              </span>
              <MetricChip metric={metric} />
            </div>
            {metric.description ? <p className="text-xs text-default-500">{metric.description}</p> : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function MetricChip({ metric }: { readonly metric: CatalogMetric }) {
  const { t } = useTranslation();
  if (!metric.certified) return <Chip size="sm" variant="flat">{t('Not certified')}</Chip>;
  const parts = [t('Certified')];
  if (metric.version !== null && metric.version !== undefined) parts[0] = `${parts[0]} v${metric.version}`;
  if (metric.owner) parts.push(metric.owner);
  return <Chip size="sm" variant="flat" color="success">{parts.join(' · ')}</Chip>;
}

export default CatalogTab;
